import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { Toaster } from "react-hot-toast";
import FeatureHeader from "./FeatureHeader";
import FeatureFilters from "./FeatureFilters";
import FeaturePagination from "./FeaturePagination";
import FeatureRequestList from "./FeatureRequestList";
import FeatureRequestDialog from "./FeatureRequestDialog";
import SubmitFeatureRequestDialog from "./SubmitFeatureRequestDialog";
import { STATUS_OPTIONS, CATEGORY_OPTIONS, buttonStyles } from "./featureConfig";
import { useFeatureRequests } from "./useFeatureRequests";
import AddIcon from "@mui/icons-material/Add";
import { useParams } from "react-router-dom";
import FeaturesChart from "./FeaturesChart";
import { useFeatureFlag } from "../../../context/FeatureFlagContext";


const FeatureRequestsPage = () => {

  const {
    filteredRequests,
    loading,
    statusFilter,
    setStatusFilter,
    categoryFilter,
    setCategoryFilter,
    searchQuery,
    setSearchQuery,
    total,
    totalPages,
    page,
    setPage,
    isDetailOpen,
    setDetailOpen,
    isSubmitOpen,
    setSubmitOpen,
    selectedRequest,
    commentText,
    setCommentText,
    adminForm,
    setAdminForm,
    canCurrentUserDelete,
    openRequestDetails,
    viewRequestDetails,
    handleToggleUpvote,
    handleSubmitComment,
    handleAdminSave,
    handleDeleteRequest,
    handleSubmitRequest,
    submitting,
    savingAdmin,
    loadRequests,
    isSuperuser,
    loadingDetails,
    submittingComment,
    upvoting,
    columnSearch,
    sortBy,
    sortOrder,
    handleColumnSearchChange,
    handleSortChange,
    apiEnums,
    enumsLoading,
    activeSearchColumn,
    setStartDate: setHookStartDate,
    setEndDate: setHookEndDate,
  } = useFeatureRequests();

  const featureId = useParams().featureId;


  const [filtersVisible, setFiltersVisible] = useState(false);
  const isFeatureschartEnabled = useFeatureFlag("featuresChart");
  const [filterTrigger, setFilterTrigger] = useState(0);

  // Date picker states
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - 30);
    return start.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    const end = new Date(today);
    end.setDate(end.getDate() + 90);
    return end.toISOString().split("T")[0];
  });

  // Sync initial page date range into the hook so hook-driven reloads include it
  useEffect(() => {
    setHookStartDate(startDate);
    setHookEndDate(endDate);
  }, []);


  const handleStatusChange = (selectedValues) => {
    setStatusFilter(selectedValues);
    setPage(1);
  };

  const handleCategoryChange = (selectedValues) => {
    setCategoryFilter(selectedValues);
    setPage(1);
  };

  const handleToggleFilters = () => {
    setFiltersVisible((prev) => !prev);
  };

  const handleResetFilters = () => {
    setStatusFilter([]);
    setCategoryFilter([]);
    setSearchQuery("");
    setPage(1);
    setFilterTrigger(prev => prev + 1);
    // Reset dates to defaults
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - 30);
    const end = new Date(today);
    end.setDate(end.getDate() + 90);
    const newStart = start.toISOString().split("T")[0];
    const newEnd = end.toISOString().split("T")[0];
    setStartDate(newStart);
    setEndDate(newEnd);
    setHookStartDate(newStart);
    setHookEndDate(newEnd);
    loadRequests({ start_date: newStart, end_date: newEnd });
  };

  const handleApplyFilters = () => {
    setPage(1);
    setHookStartDate(startDate);
    setHookEndDate(endDate);
    loadRequests({
      page: 1,
      category: categoryFilter,
      status: statusFilter,
      start_date: startDate,
      end_date: endDate,
    });
    setFilterTrigger(prev => prev + 1);
  };

  // Handle date range changes
  const handleApplyRange = (selectedStartDate, selectedEndDate) => {
    setStartDate(selectedStartDate);
    setEndDate(selectedEndDate);
    setPage(1);
    setHookStartDate(selectedStartDate);
    setHookEndDate(selectedEndDate);
    loadRequests({
      page: 1,
      category: categoryFilter,
      status: statusFilter,
      start_date: selectedStartDate,
      end_date: selectedEndDate,
    });
    setFilterTrigger(prev => prev + 1); 
  };

  const handlePageChange = (_, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const canDeleteSelected = selectedRequest
    ? canCurrentUserDelete(selectedRequest)
    : false;



  useEffect(() => {
    if (featureId) {
      viewRequestDetails(featureId);
    }
  }, [featureId]);

  const statusOptionsFromApi = [{ label: "All Statuses", value: "ALL" }].concat(
    (apiEnums?.statuses && apiEnums.statuses.length
      ? STATUS_OPTIONS
      : STATUS_OPTIONS)
  );



  const categoryOptionsFromApi = [{ label: "All Categories", value: "ALL" }].concat(
    (apiEnums?.categories && apiEnums.categories.length
      ? CATEGORY_OPTIONS
      : CATEGORY_OPTIONS)
  );

  return (

    featureId ?
      <FeatureRequestDialog
        open={isDetailOpen}
        onClose={() => setDetailOpen(false)}
        request={selectedRequest}
        canDeleteRequest={canDeleteSelected}
        commentValue={commentText}
        onCommentChange={setCommentText}
        onSubmitComment={handleSubmitComment}
        isSuperuser={isSuperuser}
        adminForm={adminForm}
        onAdminFormChange={(updates) =>
          setAdminForm((prev) => ({ ...prev, ...updates }))
        }
        onAdminSave={handleAdminSave}
        onDelete={handleDeleteRequest}
        saving={savingAdmin}
        statusOptions={statusOptionsFromApi.filter((i) => i.value !== "ALL")}
        loadingDetails={loadingDetails}
        submittingComment={submittingComment}
        goBack={true}
      /> :
      <Box
        sx={{
          width: "100%",
          px: { xs: 2, md: 2 },
          py: 2,
          backgroundColor: "#f9fafb",
          fontFamily: '"Poppins", sans-serif',
        }}
      >
        <FeatureHeader
          onRefresh={loadRequests}
          onOpenSubmit={() => setSubmitOpen(true)}
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          handleApplyRange={handleApplyRange}
        />

        {isFeatureschartEnabled && (
          <FeaturesChart
            statusFilter={statusFilter}
            categoryFilter={categoryFilter}
            columnSearch={columnSearch}
            activeSearchColumn={activeSearchColumn}
            trigger={filterTrigger}
            startDate={startDate}
            endDate={endDate}
          />
        )}
        <FeatureFilters
          statusFilter={statusFilter}
          categoryFilter={categoryFilter}
          searchQuery={searchQuery}
          total={total}
          onStatusChange={handleStatusChange}
          onCategoryChange={handleCategoryChange}
          onSearchChange={(e) => setSearchQuery(e.target.value)}
          statusOptions={statusOptionsFromApi}
          categoryOptions={categoryOptionsFromApi}
          filtersVisible={filtersVisible}
          onToggleFilters={handleToggleFilters}
          onResetFilters={handleResetFilters}
          onApplyFilters={handleApplyFilters}
          rightActions={
            <>

              <button
                className=" text-white bg-[#bb1ccc] rounded-lg hover:bg-[#bb1ccc] h-[45px] px-3 py-2.5 text-sm "
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setSubmitOpen(true)}
                sx={{ whiteSpace: "nowrap", height: "45px", borderRadius: 2 }}
              >
                <AddIcon /> Submit Request
              </button>
            </>
          }
        />
        <FeatureRequestList
          requests={filteredRequests}
          onSelect={openRequestDetails}
          onToggleUpvote={handleToggleUpvote}
          upvoting={upvoting}
          sortBy={sortBy}
          sortOrder={sortOrder}
          columnSearch={columnSearch}
          onSortChange={handleSortChange}
          onSearchChange={handleColumnSearchChange}
          loading={loading}
        />
        <FeaturePagination
          page={page}
          totalPages={totalPages}
          total={total}
          onChange={handlePageChange}
        />
        <FeatureRequestDialog
          open={isDetailOpen}
          onClose={() => setDetailOpen(false)}
          request={selectedRequest}
          canDeleteRequest={canDeleteSelected}
          commentValue={commentText}
          onCommentChange={setCommentText}
          onSubmitComment={handleSubmitComment}
          isSuperuser={isSuperuser}
          adminForm={adminForm}
          onAdminFormChange={(updates) =>
            setAdminForm((prev) => ({ ...prev, ...updates }))
          }
          onAdminSave={handleAdminSave}
          onDelete={handleDeleteRequest}
          saving={savingAdmin}
          statusOptions={statusOptionsFromApi.filter((i) => i.value !== "ALL")}
          loadingDetails={loadingDetails}
          submittingComment={submittingComment}
        />

        <SubmitFeatureRequestDialog
          open={isSubmitOpen}
          onClose={() => setSubmitOpen(false)}
          onSubmit={handleSubmitRequest}
          submitting={submitting}
          categoryOptions={categoryOptionsFromApi.filter((item) => item.value !== "ALL")}
        />
        <Toaster position="top-right" />
      </Box>


  );
};

export default FeatureRequestsPage;